import { createContext, useContext } from "react";
import { mapStrings } from "../lib/frTypo";
import type { Merchant } from "./merchants";

/**
 * Merchant video — on-screen copy, FR + EN.
 *
 * Sourced from the landing's brand page so the video and /brands/<slug>
 * say the same thing (src/i18n/locales/{fr,en}.json):
 *   - "Recommande {name}…"            → brands.hero.title / brands.cta.heading
 *   - "par recommandation complétée"  → brands.reward.perRecommendation
 *   - friend gets cashback            → brands.how.s3.body, faq.a4
 *   - "Du vrai cash. Pas des points." → /vs, same line as the B2C video
 *   - "Jusqu'à {reward} €"            → formatReward() (the landing's wording)
 *   - footnote                        → brands.reward.disclaimer, shortened
 *
 * House style (B2C): tutoiement, no em dashes, no hype words. The reward is
 * always a maximum ("jusqu'à") and always sits next to its mechanic and its
 * footnote, never alone. The notification carries no amount: the video
 * never shows a gain the viewer might not get.
 *
 * Slots: {name}, {reward}, {domain}. Filled by `fill()`.
 */

export type Lang = "fr" | "en";

export type MerchantCopy = {
  hook: { ask: string; reply: string };
  offer: {
    head: string;
    label: string;
    per: string;
    friend: string;
    friendValue: string;
  };
  payoff: {
    app: string;
    time: string;
    title: string;
    body: string;
    line1: string;
    line2: string;
    struck: string;
    bank: string;
  };
  end: {
    head: string;
    reward: string;
    cta: string;
    /** When the merchant has no consumer-facing domain (myshopify…). */
    ctaNoSite: string;
    or: string;
    appStoreSmall: string;
    appStore: string;
    playSmall: string;
    play: string;
  };
  /** Legal line under every reward amount. */
  footnote: string;
  /** Tagline when the brief has none. Empty: the showcase shows the name. */
  fallbackTagline: string;
  /** "jusqu'à" / "up to", set small before the amount. */
  upTo: string;
};

const frRaw: MerchantCopy = {
  hook: {
    ask: "Trop beau !! C'est quoi ?? 😍",
    reply: "C'est {name} ! Je t'envoie le lien 👇",
  },
  offer: {
    head: "Recommande {name} à tes proches.",
    label: "Pour toi",
    per: "à chaque commande passée via ton lien",
    friend: "Pour tes proches",
    friendValue: "du cashback",
  },
  payoff: {
    app: "FRAK",
    time: "maintenant",
    title: "Ton lien a fait une vente ! 🎉",
    body: "{name} · commande passée via ton lien",
    line1: "Du vrai cash.",
    line2: "Pas des",
    struck: "points.",
    bank: "Transférable sur ton compte",
  },
  end: {
    head: "Recommande {name}",
    reward: "et gagne jusqu'à {reward}",
    cta: "Ton lien sur {domain}",
    ctaNoSite: "Ton lien sur le site {name}",
    or: "ou dans l'app Frak",
    appStoreSmall: "Télécharger dans",
    appStore: "App Store",
    playSmall: "DISPONIBLE SUR",
    play: "Google Play",
  },
  footnote:
    "Estimation selon la campagne. Le montant dépend du panier de ton ami·e.",
  fallbackTagline: "",
  upTo: "jusqu'à",
};

const en: MerchantCopy = {
  hook: {
    ask: "Obsessed!! What is it?? 😍",
    reply: "It's {name}! Sending you the link 👇",
  },
  offer: {
    head: "Recommend {name} to your friends.",
    label: "For you",
    per: "on every order placed through your link",
    friend: "For your friends",
    friendValue: "cashback",
  },
  payoff: {
    app: "FRAK",
    time: "now",
    title: "Your link made a sale! 🎉",
    body: "{name} · order placed through your link",
    line1: "Real cash.",
    line2: "Not",
    struck: "points.",
    bank: "Transferable to your bank",
  },
  end: {
    head: "Recommend {name}",
    reward: "and earn up to {reward}",
    cta: "Get your link on {domain}",
    ctaNoSite: "Get your link on the {name} website",
    or: "or in the Frak app",
    appStoreSmall: "Download on the",
    appStore: "App Store",
    playSmall: "GET IT ON",
    play: "Google Play",
  },
  footnote:
    "Estimate based on the live campaign. The amount depends on your friend's basket.",
  fallbackTagline: "",
  upTo: "up to",
};

// French typography (no-break spaces before ! ? : € and %), see lib/frTypo.
export const COPY: Record<Lang, MerchantCopy> = { fr: mapStrings(frRaw), en };

/** "5 €" / "€5": the reward amount alone, formatted the local way. */
export const euros = (lang: Lang, n: number) =>
  lang === "fr" ? `${n}\u00A0€` : `€${n}`;

/** Fill {name} / {reward} / {domain}. `{reward}` gets the local € format. */
export const fill = (text: string, m: Merchant, lang: Lang) =>
  text
    .split("{name}")
    .join(m.name)
    .split("{reward}")
    .join(euros(lang, m.reward))
    .split("{domain}")
    .join(m.site ?? m.domain);

export type MerchantContext = {
  copy: MerchantCopy;
  lang: Lang;
  merchant: Merchant;
};

export const MerchantCtx = createContext<MerchantContext | null>(null);

/** Copy + merchant for the current render. */
export const useMerchant = () => {
  const ctx = useContext(MerchantCtx);
  if (!ctx)
    throw new Error("useMerchant() must be used inside <MerchantVideo>.");
  const t = (text: string) => fill(text, ctx.merchant, ctx.lang);
  const tagline =
    ctx.merchant.tagline?.[ctx.lang] ?? t(ctx.copy.fallbackTagline);
  return { ...ctx, t, tagline, m: ctx.merchant };
};
